import React from "react";

import { Icon, type IconName } from "./icons";

export type Tone = "neutral" | "outline" | "ok" | "warn" | "bad" | "info";

export function Pill({
  tone = "neutral",
  icon,
  title,
  children,
}: {
  tone?: Tone;
  icon?: IconName;
  title?: string;
  children: React.ReactNode;
}) {
  return (
    <span className={`ds-pill ${tone}`} title={title}>
      {icon && <Icon name={icon} size={12} />}
      {children}
    </span>
  );
}

const STATUS: Record<string, { tone: Tone; icon: IconName; label: string }> = {
  ok: { tone: "ok", icon: "check", label: "On track" },
  on_track: { tone: "ok", icon: "check", label: "On track" },
  risk: { tone: "warn", icon: "alert", label: "At risk" },
  at_risk: { tone: "warn", icon: "alert", label: "At risk" },
  over: { tone: "bad", icon: "x-circle", label: "Over" },
  bad: { tone: "bad", icon: "x-circle", label: "Failing" },
  blocked: { tone: "bad", icon: "ban", label: "Blocked" },
  paused: { tone: "neutral", icon: "pause", label: "Paused" },
  off: { tone: "neutral", icon: "minus", label: "Off" },
  info: { tone: "info", icon: "info", label: "Info" },
};

/** Health/state pill with one icon per state, so colour is never the only cue. */
export function StatusPill({
  state,
  children,
  title,
}: {
  state: keyof typeof STATUS | string;
  children?: React.ReactNode;
  title?: string;
}) {
  const s = STATUS[state] ?? STATUS.info;
  return (
    <Pill tone={s.tone} icon={s.icon} title={title}>
      {children ?? s.label}
    </Pill>
  );
}
